import { escapeRegExp } from 'lodash';

import { app } from '@/main';
import { getErrorMessage } from '@/utils/getErrorMessage';

export const treatError = (error: any, showNotification = true) => {
  try {
    const { data } = error.response;
    if (typeof data.message === 'string') {
      if (showNotification) app.$notification(data.message, 'error');
      return data.message;
    }

    if (
      Array.isArray(data.message) &&
      data.message.every((msg: any) => typeof msg === 'string')
    ) {
      for (const msg of data.message) {
        if (!msg) continue;

        if (showNotification) app.$notification(msg, 'error');
        else {
          return msg;
        }
      }

      return;
    }

    const message = data.message[0];
    const { children } = message;
    if (children) {
      const constraints = children[0]
        ? children[0].constraints
        : message.constraints;
      const firstKey = Object.keys(constraints)[0];
      const messageToShow = constraints[firstKey];

      if (showNotification) app.$notification(messageToShow, 'error');
      return messageToShow;
    } else {
      const { data } = error.response;
      const message = data.message[0];
      if (showNotification)
        app.$notification(getErrorMessage(message).toString(), 'error');

      return getErrorMessage(message).toString();
    }
  } catch (error) {
    const text = 'Oops, something went wrong. Please try again.';
    if (app && app.$notification && showNotification) {
      app.$notification(text, 'error');
    }
    return text;
  }
};

export const copyToClipboard = (str: string, onSuccess?: () => void) => {
  function fallbackCopyTextToClipboard() {
    const textArea = document.createElement('textarea');
    textArea.value = str;

    // Avoid scrolling to bottom
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
    } catch (err) {
      console.error('Fallback: Oops, unable to copy', err);
    }

    document.body.removeChild(textArea);
    if (onSuccess) onSuccess();
  }

  const navigator: any = window.navigator;
  if (!navigator.clipboard) {
    fallbackCopyTextToClipboard();
    return;
  }

  navigator.clipboard
    .writeText(str)
    .then(onSuccess)
    .catch(() => {
      fallbackCopyTextToClipboard();
    });
};

export const capitalize = (str: string) =>
  `${str.substring(0, 1).toUpperCase()}${str.substring(1)}`;

export const pluralize = (count: number, noun: string, suffix = 's') =>
  `${noun}${Number(count) !== 1 ? suffix : ''}`;

export function getFlagEmoji(countryCode: string) {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char: string) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

export const ObjectToArray: <T>(obj: {
  [key: string]: T;
}) => (T & { key: string })[] = obj =>
  Object.keys(obj).map(key => ({ ...obj[key], key }));

export const sleep = (ms: number) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

export function capitalizeWords(str: string) {
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function highlightPhrase(
  text: string,
  textSearched: string,
  searchable?: boolean
) {
  if (!searchable) {
    if (textSearched === text) {
      return `<b>${text}</b>`;
    }

    return text;
  }

  if (!textSearched) {
    return text;
  }
  let newText = text;
  let wordsForReplace: any = [];

  const auxSearch = textSearched.split(' ');
  auxSearch.forEach(word => {
    let canAddWord = true;
    wordsForReplace.forEach((expression: string) => {
      if (expression.indexOf(word) !== -1) {
        canAddWord = false;
      }
    });

    if (canAddWord && word.length > 1 && !wordsForReplace.includes(word)) {
      wordsForReplace.push(word.trim());
    }
  });

  wordsForReplace.forEach((word: string) => {
    newText = newText.replace(initRegex(word), '<b>$&</b>');
  });

  return newText;
}

function initRegex(text: string) {
  return new RegExp(escapeRegExp(text), 'ig');
}
